/**
 * SharedFxApprovalBanner — iter-170an follow-up #5.
 *
 * Sticky banner shown on the dashboard when the current user is the
 * ORIGIN of one or more shared operations whose destination proposed
 * FX/amount/kind modifications and is awaiting approval. Single-click
 * jump to the operation detail to accept or keep original.
 *
 * Dismiss is intentionally absent — these are time-bound (72h) and
 * blocking decisions; we want them in the user's face until resolved.
 * However, if the list is empty the banner is null-rendered, so it's
 * not nag-prone in normal operation.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Card } from "../ui/card";
import { Sparkles, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { tx } from "../../i18n";

export function SharedFxApprovalBanner({ lang }) {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ["shared", "pending_origin_fx_reconfirm"],
    queryFn: async () => (await api.get("/shared", {
      params: { status: "pending_origin_fx_reconfirm" },
    })).data,
    enabled: !!user?.id,
    staleTime: 30_000,
    retry: false,
  });

  // Only show operations where the current user is the ORIGIN — the
  // server returns ops where the user is either owner OR destination,
  // but the FX-approval banner is meaningful only for owners.
  const ownerOps = (data?.items || []).filter((op) => op.owner_user_id === user?.id);
  if (ownerOps.length === 0) return null;

  // Pick the most recent one to feature; if there are multiple, link
  // to the /shared list with the filter.
  const featured = ownerOps[0];
  const moreCount = ownerOps.length - 1;

  return (
    <Card
      className="mb-4 p-3 rounded-2xl border-sky-200 bg-gradient-to-r from-sky-50 to-pink-50 flex items-center gap-3 flex-wrap"
      data-testid="shared-fx-approval-banner"
    >
      <div className="h-8 w-8 rounded-full bg-sky-100 text-sky-700 grid place-items-center shrink-0">
        <Sparkles className="h-4 w-4" />
      </div>
      <div className="text-xs text-sky-900 flex-1 min-w-[200px]">
        <strong data-testid="shared-fx-approval-banner-title">
          {ownerOps.length === 1
            ? tx(lang, "An operation needs your FX approval", "Una operación necesita tu aprobación de FX", "Uma operação precisa da sua aprovação de FX")
            : tx(lang, `${ownerOps.length} operations need your FX approval`, `${ownerOps.length} operaciones necesitan tu aprobación de FX`, `${ownerOps.length} operações precisam da sua aprovação de FX`)}
        </strong>
        <div className="mt-0.5 text-sky-800/85">
          <strong>{featured.concept}</strong> · {featured.total_amount.toFixed(2)} {featured.currency}
          {moreCount > 0 && ` · +${moreCount} ${tx(lang, "more", "más", "mais")}`}
        </div>
      </div>
      <Link
        to={`/shared?op=${featured.id}`}
        data-testid="shared-fx-approval-banner-cta"
        className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold transition-colors"
      >
        {tx(lang, "Review", "Revisar", "Revisar")}
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </Card>
  );
}
